import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Check, X, Plus, GripVertical, ChevronDown, ChevronRight } from 'lucide-react'
import { router } from '@inertiajs/react'
import VariableReference from '@/components/workflow-builder/nodes/VariableReference'
import './Checklist.css'

const Checklist = ({ 
  items = [], 
  onUpdate, 
  onToggleItem, 
  taskId, 
  isCollapsible = true, 
  isCollapsed = false, 
  onToggleCollapse,
  className = "",
  showProgress = true,
  maxHeight = null,
  isCheckable = true,
  onInteraction,
  allowDrag = true,
  availableVariables = []
}) => {
  const [editingIndex, setEditingIndex] = useState(null)
  const [editingValue, setEditingValue] = useState('')
  const [newItemValue, setNewItemValue] = useState('')
  const [isAddingItem, setIsAddingItem] = useState(false)
  const [draggedIndex, setDraggedIndex] = useState(null)
  const [dragOverIndex, setDragOverIndex] = useState(null)
  const [pendingToggles, setPendingToggles] = useState(new Set())
  const [lastToggleTime, setLastToggleTime] = useState({})
  
  const editInputRef = useRef(null)
  const newItemInputRef = useRef(null)

  // Helper function to ensure checklist items have required fields
  const ensureRequiredFields = (item) => {
    const baseItem = {
      label: item.label || '',
      checked: item.checked || false,
      createdBy: item.createdBy || window.currentUser?.id?.toString() || '1',
      createdAt: item.createdAt || new Date().toISOString(),
      ...item // Preserve any other fields
    }
    
    // If item is checked, ensure checkedBy and checkedAt are present
    if (baseItem.checked && (!baseItem.checkedBy || !baseItem.checkedAt)) {
      baseItem.checkedBy = window.currentUser?.id?.toString() || '1'
      baseItem.checkedAt = new Date().toISOString()
    }
    
    return baseItem
  }

  // Position cursor at end when editing starts
  useEffect(() => {
    if (editingIndex !== null && editInputRef.current) {
      const input = editInputRef.current.querySelector('input, textarea')
      if (input) {
        setTimeout(() => {
          input.focus()
          const length = input.value.length
          input.setSelectionRange(length, length)
        }, 0)
      }
    }
  }, [editingIndex])

  // Position cursor at end when adding new item starts
  useEffect(() => {
    if (isAddingItem && newItemInputRef.current) {
      const input = newItemInputRef.current.querySelector('input, textarea')
      if (input) {
        setTimeout(() => {
          input.focus()
          const length = input.value.length
          input.setSelectionRange(length, length)
        }, 0)
      }
    }
  }, [isAddingItem])

  // Calculate progress
  const completedCount = items.filter(item => item.checked).length
  const totalCount = items.length
  const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 100

  // Handle single-click to edit
  const handleSingleClick = (index, label) => {
    if (onInteraction) onInteraction(true)
    setEditingIndex(index)
    setEditingValue(label)
  }

  // Handle edit save
  const handleEditSave = () => {
    if (editingValue.trim() && editingIndex !== null) {
      const updatedItems = [...items]
      const currentItem = ensureRequiredFields(updatedItems[editingIndex])
      updatedItems[editingIndex] = {
        ...currentItem,
        label: editingValue.trim()
      }
      onUpdate(updatedItems)
    }
    setEditingIndex(null)
    setEditingValue('')
    if (onInteraction) onInteraction(false)
  }

  // Handle edit cancel
  const handleEditCancel = () => {
    setEditingIndex(null)
    setEditingValue('')
    if (onInteraction) onInteraction(false)
  }

  // Handle key press in edit mode
  const handleEditKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault() // Prevent form submission
      handleEditSave()
    } else if (e.key === 'Escape') {
      e.preventDefault() // Prevent any default escape behavior
      handleEditCancel()
    }
  }

  // Handle adding new item
  const handleAddItem = () => {
    if (newItemValue.trim()) {
      const newItem = {
        label: newItemValue.trim(),
        checked: false,
        createdBy: window.currentUser?.id?.toString() || '1',
        createdAt: new Date().toISOString()
      }
      const updatedItems = [...items, newItem]
      onUpdate(updatedItems)
      setNewItemValue('')
      setIsAddingItem(false)
      if (onInteraction) onInteraction(false)
    } else {
      // If no value, just close the input
      setIsAddingItem(false)
      if (onInteraction) onInteraction(false)
    }
  }

  // Handle key press in add mode
  const handleAddKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault() // Prevent form submission
      handleAddItem()
    } else if (e.key === 'Escape') {
      e.preventDefault() // Prevent any default escape behavior
      setNewItemValue('')
      setIsAddingItem(false)
    }
  }

  // Handle delete item
  const handleDeleteItem = (index) => {
    const updatedItems = items.filter((_, i) => i !== index)
    onUpdate(updatedItems)
  }

  // Handle toggle item with improved debouncing
  const handleToggleItem = useCallback((index) => {
    const now = Date.now()
    const lastToggle = lastToggleTime[index] || 0
    const timeSinceLastToggle = now - lastToggle

    // Prevent rapid clicking on the same item (debounce for 500ms)
    if (pendingToggles.has(index) || timeSinceLastToggle < 500) {
      return;
    }

    // Add to pending toggles and update last toggle time
    setPendingToggles(prev => new Set([...prev, index]));
    setLastToggleTime(prev => ({ ...prev, [index]: now }));
    
    if (onToggleItem) {
      onToggleItem(index).finally(() => {
        // Remove from pending toggles when the operation completes
        setTimeout(() => {
          setPendingToggles(prev => {
            const newSet = new Set(prev);
            newSet.delete(index);
            return newSet;
          });
        }, 100); // Small delay to prevent immediate re-clicking
      });
    } else {
      const updatedItems = [...items]
      const currentItem = ensureRequiredFields(updatedItems[index])
      updatedItems[index] = {
        ...currentItem,
        checked: !currentItem.checked,
        checkedBy: !currentItem.checked ? window.currentUser?.id?.toString() : undefined,
        checkedAt: !currentItem.checked ? new Date().toISOString() : undefined
      }
      onUpdate(updatedItems)
      
      // Remove from pending toggles immediately for local updates
      setTimeout(() => {
        setPendingToggles(prev => {
          const newSet = new Set(prev);
          newSet.delete(index);
          return newSet;
        });
      }, 100); // Small delay to prevent immediate re-clicking
    }
  }, [items, onToggleItem, onUpdate, pendingToggles, lastToggleTime])

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    e.stopPropagation() // Prevent TaskNode from being dragged
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
  }

  // Handle drag start from handle only
  const handleHandleDragStart = (e, index) => {
    e.stopPropagation() // Prevent TaskNode from being dragged
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    e.stopPropagation() // Prevent TaskNode from being dragged
    setDragOverIndex(index)
  }

  const handleDrop = (e, dropIndex) => {
    e.preventDefault()
    e.stopPropagation() // Prevent TaskNode from being dragged
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      const updatedItems = [...items]
      const [draggedItem] = updatedItems.splice(draggedIndex, 1)
      updatedItems.splice(dropIndex, 0, draggedItem)
      // Immediately update the UI
      onUpdate(updatedItems)
    }
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const handleDragEnd = (e) => {
    e.stopPropagation() // Prevent TaskNode from being dragged
    setDraggedIndex(null)
    setDragOverIndex(null)
  }



  if (isCollapsible && isCollapsed) {
    return (
      <div className={`checklist-container border border-border rounded-lg p-4 bg-muted/30 ${className}`}>
        <div className="checklist-header">
          <button
            onClick={onToggleCollapse}
            className="checklist-toggle-btn flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted p-1 rounded transition-colors"
          >
            <ChevronRight size={16} />
            <span>Checklist ({completedCount}/{totalCount})</span>
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`checklist-container border border-border rounded-lg p-4 bg-muted/30 ${className}`}>
      {/* Header with progress */}
      <div className={`checklist-header ${items.length > 0 || isAddingItem ? 'mb-4' : ''}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isCollapsible && (
              <button
                type="button"
                onClick={onToggleCollapse}
                className="checklist-toggle-btn p-1 rounded text-muted-foreground hover:bg-muted transition-colors"
              >
                <ChevronDown size={16} />
              </button>
            )}
            <span className="text-sm font-semibold text-foreground">
              Checklist
            </span>
            {showProgress && isCheckable && (
              <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                {completedCount} of {totalCount} complete
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setIsAddingItem(true)
            }}
            className="add-checklist-item-btn p-1 rounded text-primary hover:bg-primary/10 transition-colors"
            title="Add checklist item"
          >
            <Plus size={16} />
          </button>
        </div>
        
        {/* Progress bar */}
        {showProgress && isCheckable && totalCount > 0 && (
          <div className="mt-2">
            <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-300 ease-in-out ${
                  progressPercentage === 100 ? 'bg-green-500' : 'bg-blue-500'
                }`}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Checklist items */}
      <div 
        className="checklist-items"
        style={{
          maxHeight: maxHeight,
          overflowY: 'auto',
        }}
      >
        {items.map((item, index) => (
          <div
            key={index}
            className={`checklist-item flex items-center gap-2 p-3 mb-2 bg-background border border-border rounded-lg transition-all duration-200 shadow-sm hover:bg-muted/50 hover:border-border/80 ${
              draggedIndex === index ? 'border-2 border-dashed border-primary opacity-50' : ''
            } ${
              dragOverIndex === index ? 'bg-muted border-t-2 border-t-primary' : ''
            } ${
              index === items.length - 1 ? 'mb-4' : ''
            }`}
            onDragOver={allowDrag ? (e) => handleDragOver(e, index) : undefined}
            onDrop={allowDrag ? (e) => handleDrop(e, index) : undefined}
            onDragEnd={allowDrag ? handleDragEnd : undefined}
          >
            {/* Drag handle */}
            {allowDrag && (
              <div 
                className="checklist-drag-handle cursor-grab p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors select-none"
                draggable={true}
                onDragStart={(e) => handleHandleDragStart(e, index)}
              >
                <GripVertical size={14} />
              </div>
            )}

            {/* Checkbox */}
            {isCheckable && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  handleToggleItem(index)
                }}
                disabled={pendingToggles.has(index)}
                className={`checklist-checkbox border-2 rounded p-1 flex items-center justify-center min-w-6 min-h-6 transition-all duration-200 ${
                  pendingToggles.has(index)
                    ? 'opacity-50 cursor-not-allowed bg-muted border-muted-foreground'
                    : item.checked 
                      ? 'bg-green-500 border-green-500 text-white hover:bg-green-600 hover:border-green-600' 
                      : 'border-border hover:bg-muted hover:border-muted-foreground'
                }`}
                title={pendingToggles.has(index) ? 'Updating...' : undefined}
              >
                {item.checked && <Check size={16} />}
                {pendingToggles.has(index) && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </button>
            )}

            {/* Item label */}
            <div 
              className="checklist-item-label flex-1 text-sm cursor-pointer p-1 rounded min-h-5 flex items-center transition-colors hover:bg-muted/50"
              style={{
                color: item.checked ? 'var(--muted-foreground)' : 'var(--foreground)',
                textDecoration: item.checked ? 'line-through' : 'none',
              }}
              onClick={(e) => {
                e.stopPropagation()
                handleSingleClick(index, item.label)
              }}
            >
              {editingIndex === index ? (
                <div ref={editInputRef} className="w-full">
                  <VariableReference
                    value={editingValue}
                    onChange={(value) => setEditingValue(value)}
                    onBlur={(e) => {
                      // Check if the blur is due to clicking on variable dropdown
                      const relatedTarget = e.relatedTarget
                      if (relatedTarget && (
                        relatedTarget.closest('.variable-menu') ||
                        relatedTarget.closest('.variable-button') ||
                        relatedTarget.closest('.variable-option')
                      )) {
                        return // Don't save if clicking on variable dropdown
                      }
                      handleEditSave()
                    }}
                    onFocus={() => { if (onInteraction) onInteraction(true) }}
                    onKeyDown={handleEditKeyPress}
                    availableVariables={availableVariables}
                    placeholder="Enter checklist item..."
                    className="w-full"
                    style={{
                      width: '100%',
                      border: '1px solid var(--primary)',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: 'var(--background)',
                      color: 'var(--foreground)'
                    }}
                  />
                </div>
              ) : (
                item.label
              )}
            </div>

            {/* Delete button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                handleDeleteItem(index)
              }}
              className="delete-checklist-item-btn p-1 rounded text-destructive opacity-70 hover:opacity-100 hover:bg-destructive/10 transition-all duration-200"
              title="Delete item"
            >
              <X size={14} />
            </button>
          </div>
        ))}

        {/* Add new item input */}
        {isAddingItem && (
          <div className="checklist-item flex items-center gap-2 p-3 mb-4 bg-muted/50 border-2 border-primary rounded-lg shadow-sm">
            <div ref={newItemInputRef} className="flex-1">
              <VariableReference
                value={newItemValue}
                onChange={(value) => setNewItemValue(value)}
                onFocus={() => { if (onInteraction) onInteraction(true) }}
                onBlur={(e) => {
                  // Check if the blur is due to clicking on variable dropdown
                  const relatedTarget = e.relatedTarget
                  if (relatedTarget && (
                    relatedTarget.closest('.variable-menu') ||
                    relatedTarget.closest('.variable-button') ||
                    relatedTarget.closest('.variable-option')
                  )) {
                    return // Don't close if clicking on variable dropdown
                  }
                  
                  if (newItemValue.trim()) {
                    handleAddItem()
                  } else {
                    setIsAddingItem(false)
                    if (onInteraction) onInteraction(false)
                  }
                }}
                onKeyDown={handleAddKeyPress}
                availableVariables={availableVariables}
                placeholder="Enter checklist item..."
                className="w-full"
                style={{
                  border: '1px solid var(--primary)',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '14px',
                  outline: 'none',
                  backgroundColor: 'var(--background)',
                  color: 'var(--foreground)'
                }}
              />
            </div>
          </div>
        )}
      </div>

    </div>
  )
}

export default Checklist 